import dayjs from 'dayjs';
import { ApplicationConfig } from '../redux/application/application.types';
import { APP_TIMEZONE, dayjsTZ } from './DateHelper';
import { JobType, JobConstraintsType } from '../redux/jobs/jobs.types';

// Documented fallbacks — see ReadingDateValidation in application.types.ts.
export const DEFAULT_READING_DATE_MAX_DAYS_IN_PAST = 30;
export const DEFAULT_READING_DATE_MAX_DAYS_IN_FUTURE = 0;

// Sentinel bounds standing in for "no constraint on this side" (missing
// earliestExecution/latestExecution) — wide enough that no real reading date
// can ever fall outside them, so the Excel AND(>=min,<=max) formula and the
// hidden __minAllowedDate/__maxAllowedDate columns don't need a conditional
// "no limit" branch.
const UNBOUNDED_MIN = dayjsTZ('1900-01-01').startOf('day');
const UNBOUNDED_MAX = dayjsTZ('9999-12-31').startOf('day');

// Berlin midnight of the day `days` calendar days after (or before) the Berlin day of
// `date`. Works on 'YYYY-MM-DD' strings: add()/subtract() on a dayjs tz object keep
// the start date's UTC offset, so a window across a DST change would end an hour off
// (in autumn on 23:00 of the previous day, rejecting the last allowed day).
const shiftedBerlinMidnight = (date: string, days: number): dayjs.Dayjs => {
    const [y, m, d] = dayjsTZ(date).format('YYYY-MM-DD').split('-').map(Number);
    const day = new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
    return dayjs.tz(day, APP_TIMEZONE);
};

export interface AllowedReadingDateRange {
    minAllowedDate: dayjs.Dayjs;
    maxAllowedDate: dayjs.Dayjs;
}

/**
 * Allowed reading-date window for a job.
 *
 * Anchored on the job's execution window (Ausführungsfrist), NOT on
 * `targetReadingDate` and NOT on the export/import day:
 *   min = earliestExecution − readingDateMaxDaysInPast
 *   max = latestExecution + readingDateMaxDaysInFuture
 *
 * The Excel file can be prepared weeks before the actual reading and filled
 * in later, so the allowed window must track when the job may be executed,
 * not when the file happened to be generated or re-imported.
 *
 * `earliestExecution`/`latestExecution` are optional. A missing side means no
 * constraint on that side (unbounded) — the same "missing value = no limit"
 * convention used elsewhere in the codebase (`isJobExpired`, JobDetails.tsx's
 * `earliestExecution`/`latestExecutionIsValid` memos, `jobDeadlineCategory`).
 */
export function getAllowedReadingDateRange(
    jobConstraints: JobConstraintsType | null | undefined,
    mergedConfig?: ApplicationConfig,
): AllowedReadingDateRange {
    const maxDaysInPast =
        mergedConfig?.meterReading?.readingDate?.readingDateMaxDaysInPast ?? DEFAULT_READING_DATE_MAX_DAYS_IN_PAST;
    const maxDaysInFuture =
        mergedConfig?.meterReading?.readingDate?.readingDateMaxDaysInFuture ?? DEFAULT_READING_DATE_MAX_DAYS_IN_FUTURE;

    const earliestExecution = jobConstraints?.earliestExecution;
    const latestExecution = jobConstraints?.latestExecution;

    return {
        minAllowedDate: earliestExecution ? shiftedBerlinMidnight(earliestExecution, -maxDaysInPast) : UNBOUNDED_MIN,
        maxAllowedDate: latestExecution ? shiftedBerlinMidnight(latestExecution, maxDaysInFuture) : UNBOUNDED_MAX,
    };
}

/**
 * Convert a day to a JS Date whose UTC wall-clock is midnight of that calendar
 * day, so ExcelJS' UTC-based date→serial conversion (`25569 + getTime()/86400000`)
 * yields the *integer* serial Excel itself produces when a user types that date.
 *
 * Passing a timezone-local midnight Date (e.g. `dayjsTZ().startOf('day').toDate()`
 * in Europe/Berlin) would be shifted by the UTC offset, so the serialised bound
 * lands at 22:00 of the previous day. A hand-typed date is an integer serial and
 * would then fail `date <= maxAllowedDate` for *every* value — the reason no
 * reading date could be entered at all.
 */
export function toExcelDateValue(day: dayjs.Dayjs): Date {
    return new Date(Date.UTC(day.year(), day.month(), day.date()));
}

/**
 * Mirrors the expiry check in JobDetails.tsx and ImportExcel.utils.ts.
 * An order is expired when today is past latestExecution + grace period.
 * Returns false (= not expired) when constraints or durationAllowed are absent.
 */
export function isJobExpired(job: JobType, durationAllowed: number | null): boolean {
    if (!job.jobConstraints || durationAllowed === null) return false;
    const latestExecution = job.jobConstraints.latestExecution;
    if (!latestExecution) return false;
    const effectiveDeadline = dayjs(latestExecution).add(durationAllowed, 'days');
    return dayjs().isAfter(effectiveDeadline);
}

export type DoneHintTranslationKey = 'jobs.details.doneHint' | 'jobs.details.doneHintLate' | 'jobs.details.doneHintOverdue';

/**
 * Which "done hint" text to show in JobDetails for a job's target reading
 * dates relative to its execution deadline. A missing `latestExecution`
 * means no deadline — same "missing value = no constraint" convention as
 * `isJobExpired` and `getAllowedReadingDateRange` — so the job is never
 * "late" or "overdue" without one.
 */
export function resolveDoneHintKey(
    targetReadingDates: number[],
    latestExecution: string | null | undefined,
): DoneHintTranslationKey {
    if (!latestExecution) return 'jobs.details.doneHint';

    const now = dayjs().valueOf();
    const latestExecutionValue = dayjs(latestExecution).valueOf();

    if (targetReadingDates.some((date) => date < now) && now < latestExecutionValue) {
        return 'jobs.details.doneHintLate';
    }
    if (now > latestExecutionValue) {
        return 'jobs.details.doneHintOverdue';
    }
    return 'jobs.details.doneHint';
}
